// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.

/**
 * Header verified-badge decision (#68, trial)
 * — pure, no I/O except the one guarded read in
 * {@link readVerifiedBadgeFromLocation}.
 *
 * TRUST RULE (same as the seat chip, #64): the `is-pilot-seat` /
 * `is-standard-seat` query-string flags are CLIENT-CONTROLLED. They are an
 * UNTRUSTED DISPLAY HINT only — never an authorization, restriction, routing
 * or audit signal. Spoofing a flag changes only what the user sees about
 * themselves.
 *
 * Read ONCE on mount and held as one session-level value in `App`, outside
 * `features/orchestrator.ts`, per-stage progress (ADR-007 D5) and
 * halt-and-report (ADR-006). Independent of the seat chip: both may show.
 */

/**
 * The badge colour: `gold` for Pilot, `navy` for Standard (dark blue,
 * `--nc-color-primary-2`). Applied to the seal fill only; the check mark is
 * always white.
 */
export type VerifiedBadgeTone = "gold" | "navy";

export interface VerifiedBadgeSpec {
  /** Accessible name only; nothing visible shows it (icon only, no tooltip). */
  readonly label: string;
  readonly tone: VerifiedBadgeTone;
}

// Display-only. Fed by untrusted query-string flags (see the Trust rule).
export const VERIFIED_BADGES = {
  pilot: { label: "Pilot user", tone: "gold" },
  standard: { label: "Standard user", tone: "navy" },
} as const satisfies Readonly<Record<string, VerifiedBadgeSpec>>;

export type VerifiedBadgeKey = keyof typeof VERIFIED_BADGES;

/** Kebab-case, the same spelling convention as the seat-chip flags (#64). */
export const VERIFIED_BADGE_QUERY_KEYS = {
  pilot: "is-pilot-seat",
  standard: "is-standard-seat",
} as const;

/**
 * The badge for the given query string, or null for none. Never throws.
 * Only the exact lowercase string "true" counts. Both flags true shows Pilot
 * only, so at most one badge is shown.
 */
export function resolveVerifiedBadge(params: URLSearchParams): VerifiedBadgeKey | null {
  if (params.get(VERIFIED_BADGE_QUERY_KEYS.pilot) === "true") return "pilot";
  if (params.get(VERIFIED_BADGE_QUERY_KEYS.standard) === "true") return "standard";
  return null;
}

/**
 * Reads the widget's own query string and resolves the badge. An unreadable
 * query string is a defined "no badge" branch, logged to the console and
 * never blocking a device query. Never throws.
 */
export function readVerifiedBadgeFromLocation(readSearch: () => string): VerifiedBadgeKey | null {
  try {
    return resolveVerifiedBadge(new URLSearchParams(readSearch()));
  } catch (error) {
    console.warn("[au0009]", "verified badge: query string unreadable; no badge shown", error);
    return null;
  }
}
